import {
    ArrowDropdownRight,
    ArrowRight,
    Icon,
    IconComponent,
    SmallIcon,
    ThreeDotsSettings,
} from "@nova/component-icons"
import { ID } from "@nova/constants"
import classNames from "classnames"
import {
    animationClass,
    defaultFolderName,
    defaultProjectName,
} from "features/data-management/workspace/constants"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { useAppSelector } from "integrations/redux/hooks"
import { MouseEvent, useEffect, useRef, useState } from "react"

type WorkspaceSidePanelTabProps = {
    title: string
    /**
     * This should be completely unique. If the parent of this component has
     * some type of key, use that. This is used for uniquely displaying the
     * RenamableText and ContextMenu.
     */
    id: ID

    icon?: IconComponent
    iconColor?: string

    isRenaming?: boolean
    handleConfirmRename?: (id: ID, name: string) => void
    handleCancelRename?: () => void

    contextMenuSections?: ContextMenuItem[][]
    handleToggleContextMenu?: (force?: boolean) => void

    // Folder stuff
    isCollapsable?: boolean
    depth?: number
    dropdownIsOpen?: boolean
    toggleDropdown?: () => void

    isHovering: boolean

    /** "Navigate" meaning leave the workspace view, not switching the workspace mode. */
    clickWillNavigate?: boolean
    isActive?: boolean
    shouldShowArrow?: boolean

    /**
     * Whether an item being dragged (and is above this component)
     * can be dropped into this component. If `undefined`, no
     * conditional styling will be applied.
     */
    draggedItemOverCanDrop?: boolean

    handleClick: (e: MouseEvent) => void
    handleRightClick?: (e: MouseEvent) => void
} & React.HTMLAttributes<HTMLElement>

export const WorkspaceSidePanelTab = ({
    title,
    id,

    icon,
    iconColor,

    isRenaming = false,
    handleConfirmRename,
    handleCancelRename,

    contextMenuSections,
    handleToggleContextMenu,

    isCollapsable = false,
    depth = 0,
    dropdownIsOpen = false,
    toggleDropdown,

    isHovering,

    clickWillNavigate = false,
    isActive = false,
    shouldShowArrow = false,

    draggedItemOverCanDrop,

    handleClick,
    handleRightClick,
    ...rest
}: WorkspaceSidePanelTabProps) => {
    const [isHoveringDropdown, setIsHoveringDropdown] = useState(false)
    const renameRef = useRef<HTMLInputElement>(null)

    const entityId = id.split("_").at(-1)
    const isFavorite = id.split("_").at(0) === "fav"
    const activeWorkspaceSidePanelContextMenu = useAppSelector(
        selectActiveContextMenu("WorkspaceSidePanel")
    )
    const contextMenuIsForThisEntity =
        activeWorkspaceSidePanelContextMenu?.data.entityId === entityId &&
        activeWorkspaceSidePanelContextMenu?.data.isFavorite === isFavorite

    const shouldShowIconHoverState =
        (!isCollapsable && isHovering) || (isCollapsable && isHoveringDropdown)
    const shouldAccentIcon = isActive || (clickWillNavigate && shouldShowIconHoverState)
    const shouldBrightenIcon =
        !shouldAccentIcon && (dropdownIsOpen || shouldShowIconHoverState) && isCollapsable
    shouldShowArrow = shouldShowArrow && isHovering && !isActive
    const shouldShow3DotsButton =
        (isHovering || contextMenuIsForThisEntity) &&
        contextMenuSections &&
        contextMenuSections.length > 0 &&
        !isRenaming
    useEffect(() => {
        if (isRenaming) renameRef.current?.focus()
    }, [isRenaming])

    return (
        <div
            {...rest}
            className={classNames(
                rest.className,
                "relative w-full h-9 rounded-lg pr-3 select-none cursor-pointer flex items-center border",
                animationClass,
                {
                    "pl-4": !isCollapsable,
                    "bg-transparent": !isHovering && !isActive,
                    "bg-gray-800": (isHovering || contextMenuIsForThisEntity) && !isActive,
                    "bg-gray-700": isActive,
                    "border-transparent": draggedItemOverCanDrop === undefined,
                    "border-blue-500": draggedItemOverCanDrop === true,
                    "border-red-500": draggedItemOverCanDrop === false,
                }
            )}
            onClick={!isRenaming ? handleClick : (e) => e.stopPropagation()}
            onContextMenu={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleRightClick?.(e)
            }}
        >
            <div
                className="flex items-center w-full gap-3"
                style={{
                    paddingLeft: 20 * depth,
                }}
            >
                {/* Icon and potentially a dropdown arrow */}
                {(isCollapsable || icon) && (
                    <div
                        className="flex items-center"
                        onMouseEnter={() => setIsHoveringDropdown(true)}
                        onMouseLeave={() => setIsHoveringDropdown(false)}
                        onClick={(e) => {
                            if (isCollapsable && toggleDropdown) {
                                e.stopPropagation()
                                toggleDropdown()
                            }
                        }}
                    >
                        {isCollapsable && (
                            <Icon
                                Icon={ArrowDropdownRight}
                                className={classNames("-mr-1 -ml-1", animationClass, {
                                    "rotate-90": dropdownIsOpen,
                                    "text-gray-400":
                                        !dropdownIsOpen && !shouldShowIconHoverState && !isRenaming,
                                    "text-gray-0":
                                        dropdownIsOpen || shouldShowIconHoverState || isRenaming,
                                })}
                            />
                        )}
                        {icon && (
                            <Icon
                                Icon={icon}
                                className={classNames(/*iconClass,*/ animationClass, {
                                    "text-gray-400":
                                        !shouldAccentIcon &&
                                        !shouldBrightenIcon &&
                                        !iconColor &&
                                        !isRenaming,
                                    "text-gray-50": shouldBrightenIcon || isRenaming,
                                    "text-blue-500": shouldAccentIcon && !isRenaming,
                                })}
                                style={{
                                    color:
                                        (!shouldAccentIcon && !shouldBrightenIcon) || isRenaming
                                            ? iconColor
                                            : undefined,
                                }}
                            />
                        )}
                    </div>
                )}

                {/* Title and Right-Side Buttons */}
                <div className={classNames("flex items-center w-full !h-6 min-w-0")}>
                    <RenamableText
                        ref={renameRef}
                        Tag="h5"
                        title={title}
                        id={id}
                        isRenaming={isRenaming}
                        initialTitleIsPlaceholder={
                            title === defaultFolderName || title === defaultProjectName
                        }
                        isFullWidth={true}
                        backgroundIsDark={isActive}
                        className={classNames("!h-6", animationClass, {
                            "text-gray-100": !isHovering && !isActive && !isRenaming,
                            "text-gray-0": isHovering || isActive || isRenaming,
                        })}
                        onConfirmRenaming={handleConfirmRename}
                        onCancelRenaming={handleCancelRename}
                    />

                    <div className="flex flex-row gap-3">
                        {/* 3 Dots Button */}
                        {shouldShow3DotsButton && (
                            <SmallIcon
                                Icon={ThreeDotsSettings}
                                className={classNames(
                                    "text-gray-300 hover:text-gray-0",
                                    animationClass
                                )}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (handleToggleContextMenu) handleToggleContextMenu()
                                }}
                            />
                        )}

                        {shouldShowArrow && !isRenaming && (
                            <SmallIcon
                                Icon={ArrowRight}
                                className={classNames(
                                    "text-blue-500 hover:text-blue-200",
                                    animationClass
                                )}
                                onClick={!isRenaming ? handleClick : (e) => e.stopPropagation()}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Context Menu */}
            {contextMenuIsForThisEntity && contextMenuSections && handleToggleContextMenu && (
                <ContextMenu
                    sections={contextMenuSections}
                    onBlur={() => handleToggleContextMenu(false)}
                    isFixedPosition={activeWorkspaceSidePanelContextMenu.data.fixedPosition}
                    style={
                        !activeWorkspaceSidePanelContextMenu.data.fixedPosition
                            ? { top: "2.5rem", right: "0.25rem" }
                            : undefined
                    }
                    top={activeWorkspaceSidePanelContextMenu.position.top}
                    left={activeWorkspaceSidePanelContextMenu.position.left}
                />
            )}
        </div>
    )
}
